/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useMutation, useQueryClient} from 'react-query';
import {deleteSSOConfiguration} from '@ion/core/src/services/Connect';
import {QueryKey} from '../config/QueryKeys';
import {createMutationResult} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseDeleteSSOConfigurationResult = UseDefaultQueryResult & {
  deleteSSOConfig: (ssoConfigId: string) => void;
}

const api = async(id: string) => {
  const deleted = await deleteSSOConfiguration(id);
  return deleted;
};

/**
 * Common hook for deleting a SSO Configuration entity.
 *
 * ```typescript
 *
 *    import useDeleteSSOConfiguration from '@ion/query/src/hooks/';
 *
 *    const {deleteSSOConfig, isSuccess, isError, isFetching} = useDeleteSSOConfiguration();
 * 
 *    deleteSSOConfig(ssoConfigId);
 *
 * ```
 */
export const useDeleteSSOConfiguration = (): UseDeleteSSOConfigurationResult => {
  const queryClient = useQueryClient();
  const mutation    = useMutation(api, {
    onSuccess : () => {
      queryClient.invalidateQueries([QueryKey.DELETE_SSO_CONFIGURATION]);
    }
  });
  const deleteSSOConfig = useCallback((id: string) => {
    mutation.mutate(id);
  }, [mutation]);
  return createMutationResult(
    mutation, {deleteSSOConfig}
  ) as UseDeleteSSOConfigurationResult;
};
export default useDeleteSSOConfiguration;
